import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { DesignationAreaChart } from "react-migration/domains/constraints/designation/components/DesignationAreaChart";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../../types";
import { useFloodingKeyIndicators } from "./useFloodingKeyIndicators";
import { useFloodingSummaryQuery, useSurfaceFloodingSummaryQuery } from "./useFloodingSummaryQuery";
import { Alert } from "react-migration/components/Alert";
import { FloodingLayerSummarySection } from "./FloodingLayerSummarySection";
import { useMemo, useState } from "react";
import turfArea from "@turf/area";
import { Switch } from "react-migration/components/Switch";
import { useMaxSelectionArea } from "../../utils/useMaxSelectionArea";

export const MAX_AREA_CONSTRAINT_HECTARES = 200;

function SurfaceFloodingSummary({
  surfaceFloodCoverage,
  surfaceFloodClimateCoverage,
  totalArea,
}: Pick<ReturnType<typeof useSurfaceFloodingSummaryQuery>, "surfaceFloodCoverage" | "surfaceFloodClimateCoverage"> & {
  totalArea: number;
}) {
  const { t } = useTranslation();
  const [showClimate, setShowClimate] = useState(false);

  const selectedSurfaceFloodCoverage = showClimate ? surfaceFloodClimateCoverage : surfaceFloodCoverage;

  const unaffectedSurfaceFloodArea = useMemo(() => {
    const accumulativeArea = selectedSurfaceFloodCoverage.reduce(
      (acc, { category_coverage_area }) => acc + category_coverage_area,
      0
    );
    return totalArea - accumulativeArea;
  }, [selectedSurfaceFloodCoverage, totalArea]);

  const unaffectedSurfaceFloodAreaPercentage = useMemo(() => {
    const accumulativePercentage = selectedSurfaceFloodCoverage.reduce(
      (acc, { category_coverage_percentage }) => acc + category_coverage_percentage,
      0
    );
    return 100 - accumulativePercentage;
  }, [selectedSurfaceFloodCoverage]);

  return (
    <>
      <DesignationAreaChart
        areas={selectedSurfaceFloodCoverage}
        heading={t("workbench.summary.flooding.surface_flood_heading")}
        tooltip={
          <>
            <p className="my-2">Surface flood categorises the likelihood of flooding from surface water, scored as:</p>
            <ul className="list-disc pl-9 my-2">
              <li>
                <span className="font-bold">High Risk:</span> Significant risk of flooding, requiring mitigation
                measures.
              </li>
              <li>
                <span className="font-bold">Medium Risk:</span> Moderate risk, with careful planning needed.
              </li>
              <li>
                <span className="font-bold">Low Risk:</span> Low likelihood of flooding but worth monitoring.
              </li>
            </ul>
          </>
        }
      >
        <div className="flex-1 flex justify-end items-center gap-x-2">
          <Switch
            id="show-climate"
            size="small"
            value={showClimate}
            onToggle={() => setShowClimate((c) => !c)}
            label="Include climate change"
            showLabel
            showLabelClass="text-xs pl-2 text-neutral-500"
          />
        </div>
      </DesignationAreaChart>
      <div className="grid grid-cols-2 gap-2">
        {selectedSurfaceFloodCoverage[0] && (
          <FloodingLayerSummarySection
            percentageCovered={selectedSurfaceFloodCoverage[0].category_coverage_percentage}
            areaCovered={selectedSurfaceFloodCoverage[0].category_coverage_area}
            subCategory={selectedSurfaceFloodCoverage[0].sub_category}
          />
        )}
        <FloodingLayerSummarySection
          areaCovered={unaffectedSurfaceFloodArea}
          percentageCovered={unaffectedSurfaceFloodAreaPercentage}
        />
      </div>
    </>
  );
}

export function FloodingLayerSummary({ selection, onLoadingChange, print, expanded }: LayerTypeLayerSummaryProps) {
  const { t } = useTranslation();
  const { exceedsMaximumAreaConstraint } = useMaxSelectionArea(selection, MAX_AREA_CONSTRAINT_HECTARES);
  const { floodRiskCoverage, floodZoneCoverage, error, loading } = useFloodingSummaryQuery(
    selection?.feature?.geometry
  );

  const {
    surfaceFloodCoverage,
    surfaceFloodClimateCoverage,
    loading: surfaceLoading,
    error: surfaceError,
  } = useSurfaceFloodingSummaryQuery(selection?.feature?.geometry, {
    skip: !expanded || exceedsMaximumAreaConstraint,
  });

  const totalArea = useMemo(() => {
    const geometry = selection?.feature?.geometry;
    if (!geometry) return 0;
    return turfArea(geometry);
  }, [selection?.feature?.geometry]);

  const lowProbabilityFloodZoneArea = useMemo(() => {
    const accumulativeArea = floodZoneCoverage.reduce(
      (acc, { category_coverage_area }) => acc + category_coverage_area,
      0
    );
    return totalArea - accumulativeArea;
  }, [floodZoneCoverage, totalArea]);

  const lowProbabilityFloodZoneAreaPercentage = useMemo(() => {
    const accumulativePercentage = floodZoneCoverage.reduce(
      (acc, { category_coverage_percentage }) => acc + category_coverage_percentage,
      0
    );
    return 100 - accumulativePercentage;
  }, [floodZoneCoverage]);

  const unaffectedFloodRiskArea = useMemo(() => {
    const accumulativeArea = floodRiskCoverage.reduce(
      (acc, { category_coverage_area }) => acc + category_coverage_area,
      0
    );
    return totalArea - accumulativeArea;
  }, [floodRiskCoverage, totalArea]);

  const unaffectedFloodRiskAreaPercentage = useMemo(() => {
    const accumulativePercentage = floodRiskCoverage.reduce(
      (acc, { category_coverage_percentage }) => acc + category_coverage_percentage,
      0
    );
    return 100 - accumulativePercentage;
  }, [floodRiskCoverage]);

  useSelectionSidebarLoading(onLoadingChange, loading);
  useFloodingKeyIndicators(floodZoneCoverage);

  if (loading || (surfaceLoading && !floodZoneCoverage.length && !floodRiskCoverage.length)) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading rows={3} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  if (error) {
    // caught by error boundary
    throw error;
  }

  if (
    !floodRiskCoverage?.length &&
    !floodZoneCoverage?.length &&
    !surfaceFloodCoverage?.length &&
    !surfaceFloodClimateCoverage?.length
  ) {
    return !print ? <Alert.Info>Selection is not impacted by risk of flooding</Alert.Info> : null;
  }

  return (
    <div className="flex flex-col gap-y-4">
      <DesignationAreaChart
        areas={floodZoneCoverage}
        heading={t("workbench.summary.flooding.flood_zone_heading")}
        noneLabel="Zone 1"
        tooltip={
          <>
            <p className="my-2">Designated flood zones in the UK are categorised as follows:</p>
            <ul className="list-disc pl-9 my-2">
              <li>
                <span className="font-bold">Flood Zone 1:</span> Low Probability (&lt;0.1%)
              </li>
              <li>
                <span className="font-bold">Flood Zone 2:</span> Medium Probability (0.1%–1%)
              </li>
              <li>
                <span className="font-bold">Flood Zone 3:</span> High Probability (&gt;1%)
              </li>
            </ul>
          </>
        }
      />
      <div className="grid grid-cols-2 gap-2">
        {floodZoneCoverage[0] && (
          <FloodingLayerSummarySection
            percentageCovered={floodZoneCoverage[0].category_coverage_percentage}
            areaCovered={floodZoneCoverage[0].category_coverage_area}
            subCategory={floodZoneCoverage[0].sub_category}
          />
        )}
        <FloodingLayerSummarySection
          areaCovered={lowProbabilityFloodZoneArea}
          percentageCovered={lowProbabilityFloodZoneAreaPercentage}
        />
      </div>
      <DesignationAreaChart
        areas={floodRiskCoverage}
        heading={t("workbench.summary.flooding.flood_risk_heading")}
        tooltip={
          <>
            <p className="my-2">Flood risk categorises the likelihood of flooding from seas and rivers, scored as:</p>
            <ul className="list-disc pl-9 my-2">
              <li>
                <span className="font-bold">High Risk:</span> Significant risk of flooding, requiring mitigation
                measures.
              </li>
              <li>
                <span className="font-bold">Medium Risk:</span> Moderate risk, with careful planning needed.
              </li>
              <li>
                <span className="font-bold">Low Risk:</span> Low likelihood of flooding but worth monitoring.
              </li>
              <li>
                <span className="font-bold">Very Low Risk:</span> Minimal risk, often requiring no additional action.
              </li>
            </ul>
          </>
        }
      />
      <div className="grid grid-cols-2 gap-2">
        {floodRiskCoverage[0] && (
          <FloodingLayerSummarySection
            percentageCovered={floodRiskCoverage[0].category_coverage_percentage}
            areaCovered={floodRiskCoverage[0].category_coverage_area}
            subCategory={floodRiskCoverage[0].sub_category}
          />
        )}
        <FloodingLayerSummarySection
          areaCovered={unaffectedFloodRiskArea}
          percentageCovered={unaffectedFloodRiskAreaPercentage}
        />
      </div>

      {exceedsMaximumAreaConstraint ? (
        <Alert.Warning>
          To view surface water flooding insights, please select sites that are less than {MAX_AREA_CONSTRAINT_HECTARES}{" "}
          hectares
        </Alert.Warning>
      ) : surfaceError ? (
        <Alert.Error>There was a problem loading surface water flooding.</Alert.Error>
      ) : surfaceLoading ? (
        <div className="relative p-2 h-[168px]">
          <SkeletonLoading rows={1} showPadding={false} bgClassName="bg-transparent" />
        </div>
      ) : (
        <SurfaceFloodingSummary
          surfaceFloodCoverage={surfaceFloodCoverage}
          surfaceFloodClimateCoverage={surfaceFloodClimateCoverage}
          totalArea={totalArea}
        />
      )}
    </div>
  );
}

FloodingLayerSummary.icon = "icon-lt-drop-line";
FloodingLayerSummary.supportedRegions = [SupportedRegions.england, SupportedRegions.scotland, SupportedRegions.wales];
